import { Language, LanguageType } from '../../constants/enums';
import { en } from './en';

/**
 * Shape every locale must implement. Leaf values are plain strings; copy that
 * needs runtime values is expressed as a function so call sites stay
 * type-checked without a templating mini-language. Keys are added here (and to
 * every locale file) only when a component actually renders them.
 */
export interface Dictionary {
  brand: string;
  nav: {
    home: string;
    vrMods: string;
    uevrMods: string;
    headTrackingMods: string;
    mods: string;
    games: string;
    simrlabs: string;
    underConstruction: string;
    youtubeChannel: string;
    youtubeCommunity: string;
    mainNavigation: string;
    communityLinks: string;
  };
  app: {
    loading: string;
  };
  error: {
    failedToLoad: string;
    returnHome: string;
  };
  mods: {
    featuredTitle: string;
    noResults: string;
    loadError: string;
    moddedBy: (args: { author: string }) => string;
    forGame: (args: { game: string }) => string;
    thirdPartyNotice: string;
    categoryFilters: string;
    sortByLabel: string;
    sortByUpdated: string;
    sortByCreated: string;
    searchPlaceholder: string;
    searchAriaLabel: string;
    clearSearch: string;
    noSearchResults: (args: { query: string }) => string;
    playVideoFor: (args: { title: string }) => string;
    closeVideoFor: (args: { title: string }) => string;
    profile: {
      backToMods: string;
      storePage: string;
      mediaTitle: string;
      videoTab: string;
      imagesTab: string;
      descriptionTitle: string;
      installationGuideTitle: string;
      knownIssuesTitle: string;
      privacyTitle: string;
      disclaimerTitle: string;
      creditsTitle: string;
      downloadsTitle: string;
      downloadNamed: (args: { name: string }) => string;
      galleryThumbnails: string;
      scrollThumbnailsLeft: string;
      scrollThumbnailsRight: string;
      showImageNamed: (args: { index: number }) => string;
      showVideoNamed: (args: { index: number }) => string;
      videoLabel: (args: { index: number }) => string;
      videoPreviewFor: (args: { title: string }) => string;
      thumbnailAlt: (args: { title: string }) => string;
      videoPreviewAlt: (args: { title: string; index: number }) => string;
      imagePreviewAlt: (args: { title: string; index: number }) => string;
      galleryThumbnailAlt: (args: { index: number }) => string;
      inPageNavTitle: string;
      quickDownloadsTitle: string;
    };
    linkTypes: {
      internal: string;
      external: string;
      web: string;
      github: string;
      community: string;
      discord: string;
      other: string;
    };
  };
  privacy: {
    title: string;
    lastUpdated: string;
    zeroDataTitle: string;
    zeroDataDesc: string;
    localStorageTitle: string;
    localStorageDesc: string;
    disclaimerTitle: string;
    disclaimerBox: string;
    disclaimerItem1: string;
    disclaimerItem2: string;
    aiContentTitle: string;
    aiContentDesc: string;
  };
  games: {
    backToGames: string;
    gameNotFound: string;
    backToHome: string;
    loading: (args?: { title?: string }) => string;
    loadingSubtitle: string;
    hero: {
      originalTitle: string;
      inDevelopment: string;
      wishlistSteam: string;
      watchTrailer: string;
      joinDiscord: string;
      requestAlpha: string;
      alphaModalTitle: string;
      alphaModalDesc: string;
      alphaModalEmailPlaceholder: string;
      alphaModalSubmit: string;
      alphaModalSuccess: string;
      alphaModalClose: string;
      vrSupportLabel: string;
      flatScreenLabel: string;
      engineLabel: string;
      developerLabel: string;
      publisherLabel: string;
      genreLabel: string;
      releaseTargetLabel: string;
      platformsLabel: string;
    };
    sectionNavLabel: string;
    tabs: {
      overview: string;
      features: string;
      media: string;
      roadmap: string;
    };
    overview: {
      aboutTitle: string;
      visionTitle: string;
      visionSubtitle: string;
      pillarsTitle: string;
      developerNoteTitle: string;
      developerNoteAuthor: string;
      developerNoteText: string;
      communityTitle: string;
      communityDesc: string;
    };
    pillars: {
      vrRiggingTitle: string;
      vrRiggingDesc: string;
      hydrodynamicsTitle: string;
      hydrodynamicsDesc: string;
      windTitle: string;
      windDesc: string;
      navigationTitle: string;
      navigationDesc: string;
    };
    vessels: {
      title: string;
      subtitle: string;
      sloopName: string;
      sloopType: string;
      sloopDesc: string;
      sloopSpecSpeed: string;
      sloopSpecCrew: string;
      sloopSpecHandling: string;
      ketchName: string;
      ketchType: string;
      ketchDesc: string;
      ketchSpecSpeed: string;
      ketchSpecCrew: string;
      ketchSpecHandling: string;
      assetsDisclaimer: string;
    };
    media: {
      title: string;
      subtitle: string;
      allTab: string;
      videoTab: string;
      screenshotsTab: string;
      videoBadge: string;
      openFullscreen: string;
      closeFullscreen: string;
      dismissViewer: string;
      filterLabel: string;
      thumbnailsLabel: string;
      videoPreviewFor: (args: { title: string }) => string;
      imagePreviewAlt: (args: { title: string; index: number }) => string;
      thumbnailAlt: (args: { title: string; index: number }) => string;
      prevMedia: string;
      nextMedia: string;
    };
    roadmap: {
      title: string;
      subtitle: string;
      statusCompleted: string;
      statusInProgress: string;
      statusPlanned: string;
    };
    disclaimer: {
      title: string;
      aiDisclosureText: (args: { title: string }) => string;
    };
  };
  home: {
    heroTitlePart1: string;
    heroTitlePart2: string;
    heroSubtitle: string;
    exploreModsBtn: string;
    featuredGamesBtn: string;
    featuredHeading: string;
    featuredSubtitle: string;
    viewAllMods: string;
    loadingFeaturedMods: string;
    gameSpotlightBadge: string;
    gameSpotlightHeading: string;
    gameSpotlightDesc: string;
    gameSpotlightCta: string;
    ctaButton: string;
  };
  simrlabs: {
    pageTitle: string;
    pageSubtitle: string;
    gamesHeading: string;
    gamesSubtitle: string;
    modsHeading: string;
    modsSubtitle: string;
    exploreGameBtn: string;
    noGamesFound: string;
    noModsFound: string;
    loadError: string;
    loadingGames: string;
    loadingMods: string;
  };
  footer: {
    copy: (args: { year: number }) => string;
    privacy: string;
  };
  seo: {
    homeTitle: string;
    homeDescription: string;
    privacyDescription: string;
    modProfileDescription: (args: { title: string }) => string;
    gameProfileDescription: (args: { title: string }) => string;
    simrlabsTitle: string;
    simrlabsDescription: string;
  };
}

/** Locale used before any user selection and as the fallback for unknown codes. */
export const DEFAULT_LANGUAGE: LanguageType = Language.EN;

/** Registry of every bundled locale. Add new languages here. */
export const dictionaries: Record<LanguageType, Dictionary> = {
  [Language.EN]: en,
};

/**
 * Resolve the dictionary for a language, falling back to {@link DEFAULT_LANGUAGE}
 * for any code that is not registered.
 */
export const getDictionary = (language: LanguageType): Dictionary =>
  dictionaries[language] ?? dictionaries[DEFAULT_LANGUAGE];
